/**
 * TOSS Web Application — Client Portal badge counters
 *
 * The two unread counters the portal chrome shows: notifications (bell + menu item)
 * and the company chat (menu item). Nothing else in the portal polls.
 *
 * These live in `hooks/` rather than in `features/client/**` because the ESLint boundary
 * (decision Р6) makes `features/client/**` unimportable from anywhere else — and the
 * consumers are `ClientHeader`, `ClientSidebar` and `ClientBottomNav`, which are layout
 * chrome under `components/layout/`.
 *
 * **Never** reuse `useUnreadCount()` / `useMessengerUnreadCount()` here: both call
 * `/api/lk/*`, which answers 403 for this audience. Different endpoints, different query
 * keys (`clientKeys`, not `queryKeys`) — sharing either would cross the two audiences'
 * caches on one browser.
 *
 * Polling only, no SignalR: release Р1 ships no realtime for the portal chrome, so a
 * 60-second interval is the freshness contract. `refetchIntervalInBackground: false`
 * keeps a backgrounded tab quiet.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { clientMessengerApi } from '@/api/clientPortal/clientMessenger.api';
import { clientNotificationsApi } from '@/api/clientPortal/clientNotifications.api';
import type { ClientChatListResponse, ClientUnreadCountDto } from '@/api/clientPortal/clientPortal.types';
import { clientKeys } from '@/hooks/api/clientPortal/clientQueryKeys';
import { useAuthStore } from '@/store/authStore';
import { UserRole } from '@/types';

// ============================================================================
// Constants
// ============================================================================

/** Badge polling interval. */
const BADGE_REFETCH_INTERVAL_MS = 60_000;

/** How long a fetched counter is considered fresh. */
const BADGE_STALE_TIME_MS = 30_000;

// ============================================================================
// Types
// ============================================================================

export interface UseClientBadgeOptions {
  /**
   * Override the "is this a signed-in client session" gate.
   * Leave unset — the default is what the chrome wants.
   */
  enabled?: boolean;
}

// ============================================================================
// Shared gate
// ============================================================================

/**
 * Whether portal queries may run: an authenticated session whose role is `Client`.
 *
 * The role check is not redundant with authentication. The portal chrome only renders
 * inside `ClientLayout`, but an internal user who lands on a portal URL for one render
 * (a stale link, a mid-flight guard redirect) must not fire `/api/client/*` calls that
 * would 403 and pollute their console.
 */
function useIsClientSession(): boolean {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isClient = useAuthStore((state) => state.user?.role === UserRole.Client);
  return isAuthenticated && isClient;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Unread notification count for the bell badge.
 *
 * `GET /api/client/notifications/unread-count`.
 *
 * @example
 * ```tsx
 * const { data } = useClientNotificationsUnread();
 * const count = data?.count ?? 0;
 * ```
 */
export function useClientNotificationsUnread(
  options?: UseClientBadgeOptions
): UseQueryResult<ClientUnreadCountDto, Error> {
  const isClientSession = useIsClientSession();

  return useQuery<ClientUnreadCountDto, Error>({
    queryKey: clientKeys.notifications.unreadCount(),
    queryFn: () => clientNotificationsApi.getUnreadCount(),
    enabled: options?.enabled ?? isClientSession,
    staleTime: BADGE_STALE_TIME_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: BADGE_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * The chat list, whose `totalUnreadCount` drives the «Сообщения» badge.
 *
 * `GET /api/client/messenger/chats`. It used to poll the single chat's card, which stopped being
 * an answer the day a company could hold several: the badge means «unread in this company», and
 * the server counts it across every chat of the ACTIVE one.
 *
 * Keyed on `clientKeys.chat.list('')` — the UNFILTERED entry, the same one the list screen reads
 * when nobody is searching. TanStack then de-duplicates the two into one request. When somebody
 * IS searching, the screen reads a different entry and this one keeps its own number, which is
 * the point: a badge that fell to zero while a person typed would be reporting the search, not
 * the company.
 *
 * It is a pure read. Its predecessor used to provision the chat as a side effect, which turned a
 * once-a-minute poll into a once-a-minute writer.
 */
export function useClientChatUnread(
  options?: UseClientBadgeOptions
): UseQueryResult<ClientChatListResponse, Error> {
  const isClientSession = useIsClientSession();

  return useQuery<ClientChatListResponse, Error>({
    queryKey: clientKeys.chat.list(),
    queryFn: () => clientMessengerApi.getChats(),
    enabled: options?.enabled ?? isClientSession,
    staleTime: BADGE_STALE_TIME_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: BADGE_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * Both counters at once, already unwrapped — what the navigation actually renders.
 *
 * @example
 * ```tsx
 * const { notificationsCount, chatCount } = useClientNavBadges();
 * ```
 */
export function useClientNavBadges(): {
  notificationsCount: number;
  chatCount: number;
} {
  const { data: notifications } = useClientNotificationsUnread();
  const { data: chats } = useClientChatUnread();

  return {
    notificationsCount: notifications?.count ?? 0,
    chatCount: chats?.totalUnreadCount ?? 0,
  };
}
